import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { ToastService } from 'src/app/core/services/toast.service';
import { ZoneService } from 'src/app/core/services/zone.service';
import { environment } from 'src/environments/environment';

interface CentreTri {
  id: number;
  nom: string;
  villeId?: number;
  villeNom?: string;
  telephone?: string;
  email?: string;
  responsableId?: number;
  responsableNom?: string;
  zoneIds?: number[];
  zoneNoms?: string[];
  actif: boolean;
}

@Component({
  selector: 'app-liste-centres-tri',
  templateUrl: './liste-centres-tri.component.html',
  styleUrls: ['./liste-centres-tri.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ListeCentresTriComponent implements OnInit {
  centres: CentreTri[] = [];
  centresFiltres: CentreTri[] = [];
  zones: any[] = [];
  villes: any[] = [];
  responsablesZone: any[] = [];

  get zonesFiltreesParVille(): any[] {
    if (!this.formulaire.villeId) return this.zones;
    return this.zones.filter(z => z.villeId === this.formulaire.villeId);
  }

  recherche = '';
  filtreActif: string = '';
  chargement = false;
  sauvegardeEnCours = false;
  afficherModal = false;
  modeEdition = false;
  idEdition: number | null = null;

  formulaire: Partial<CentreTri & { zoneIds: number[] }> = {
    nom: '', villeId: undefined, telephone: '', email: '',
    responsableId: undefined, zoneIds: [], actif: true
  };

  private readonly apiUrl = `${environment.apiurl}centres-tri`;

  constructor(
    private http: HttpClient,
    private toast: ToastService,
    private zoneService: ZoneService
  ) {}

  ngOnInit(): void {
    this.charger();
    forkJoin({
      zones: this.zoneService.obtenirToutesLesZones(),
      villes: this.http.get<any[]>(`${environment.apiurl}villes`),
      responsables: this.http.get<any[]>(`${environment.apiurl}users/role/RESPONSABLE_ZONE`)
    }).subscribe({
      next: ({ zones, villes, responsables }) => {
        this.zones = zones;
        this.villes = Array.isArray(villes) ? villes : [];
        this.responsablesZone = Array.isArray(responsables) ? responsables : [];
      },
      error: () => {}
    });
  }

  charger(): void {
    this.chargement = true;
    this.http.get<any>(`${this.apiUrl}/liste-admin`).subscribe({
      next: (data) => {
        this.centres = Array.isArray(data) ? data : (data?.content ?? []);
        this.appliquerFiltres();
        this.chargement = false;
      },
      error: () => { this.chargement = false; this.toast.erreur('Impossible de charger les centres de tri'); }
    });
  }

  appliquerFiltres(): void {
    let result = [...this.centres];
    const q = this.recherche.toLowerCase().trim();
    if (q) result = result.filter(c =>
      c.nom.toLowerCase().includes(q) ||
      (c.villeNom || '').toLowerCase().includes(q) ||
      (c.responsableNom || '').toLowerCase().includes(q)
    );
    if (this.filtreActif === 'actif') result = result.filter(c => c.actif);
    if (this.filtreActif === 'inactif') result = result.filter(c => !c.actif);
    this.centresFiltres = result;
  }

  ouvrirCreation(): void {
    this.modeEdition = false; this.idEdition = null;
    this.formulaire = { nom: '', villeId: undefined, telephone: '', email: '', responsableId: undefined, zoneIds: [], actif: true };
    this.afficherModal = true;
  }

  ouvrirEdition(c: CentreTri): void {
    this.modeEdition = true; this.idEdition = c.id;
    this.formulaire = { ...c, zoneIds: c.zoneIds ? [...c.zoneIds] : [] };
    this.afficherModal = true;
  }

  fermerModal(): void { this.afficherModal = false; }

  onVilleChange(): void {
    const zonesDisponibles = this.zonesFiltreesParVille.map(z => z.id);
    this.formulaire.zoneIds = (this.formulaire.zoneIds || []).filter(id => zonesDisponibles.includes(id));
  }

  toggleZone(zoneId: number): void {
    const ids = this.formulaire.zoneIds || [];
    const idx = ids.indexOf(zoneId);
    if (idx === -1) ids.push(zoneId);
    else ids.splice(idx, 1);
    this.formulaire.zoneIds = [...ids];
  }

  zoneSelectionnee(zoneId: number): boolean {
    return (this.formulaire.zoneIds || []).includes(zoneId);
  }

  sauvegarder(): void {
    if (!this.formulaire.nom?.trim()) { this.toast.erreur('Le nom est obligatoire'); return; }
    this.sauvegardeEnCours = true;
    const body = {
      nom: this.formulaire.nom,
      villeId: this.formulaire.villeId ?? null,
      telephone: this.formulaire.telephone,
      email: this.formulaire.email,
      responsableId: this.formulaire.responsableId ?? null,
      zoneIds: this.formulaire.zoneIds ?? [],
      actif: this.formulaire.actif
    };
    const req$ = this.modeEdition
      ? this.http.put<CentreTri>(`${this.apiUrl}/modifierCentreTri/${this.idEdition}`, body)
      : this.http.post<CentreTri>(`${this.apiUrl}/enregistrerCentreTri`, body);
    req$.subscribe({
      next: () => {
        this.toast.succes(this.modeEdition ? 'Centre de tri mis à jour' : 'Centre de tri créé');
        this.fermerModal(); this.charger(); this.sauvegardeEnCours = false;
      },
      error: () => { this.toast.erreur('Erreur lors de l\'enregistrement'); this.sauvegardeEnCours = false; }
    });
  }

  activerDesactiver(c: CentreTri): void {
    const action = c.actif ? 'desactiverCentreTri' : 'activerCentreTri';
    this.http.put<CentreTri>(`${this.apiUrl}/${action}/${c.id}`, {}).subscribe({
      next: () => { this.toast.succes(c.actif ? 'Centre désactivé' : 'Centre activé'); this.charger(); },
      error: () => this.toast.erreur('Erreur lors du changement de statut')
    });
  }

  supprimer(c: CentreTri): void {
    if (!confirm(`Supprimer le centre "${c.nom}" ?`)) return;
    this.http.delete(`${this.apiUrl}/supprimerCentreTri/${c.id}`).subscribe({
      next: () => { this.toast.succes('Centre de tri supprimé'); this.charger(); },
      error: () => this.toast.erreur('Impossible de supprimer ce centre')
    });
  }

  get actifs(): number { return this.centres.filter(c => c.actif).length; }
  get inactifs(): number { return this.centres.filter(c => !c.actif).length; }
}
